// Item 2: the encryption-password prompt shown when the user first enables
// "Encrypt" on a device that has no unlocked password yet.
//
// Two modes:
//   - 'init'   the account has NO encryption password yet → create the ONE-TIME
//              password (server stores only an Argon2id verifier; it can never
//              be changed afterwards — 409 on any later attempt).
//   - 'unlock' the account already has one (e.g. this is a new device/login)
//              → enter it; verified against the server before being kept in
//              sessionStorage for this browsing session only.
//
// On success the plain password is handed back to the caller, which stores it
// in sessionStorage and turns the encrypt toggle on.
import { useState, type FormEvent } from 'react';
import { setEncPassword, verifyEncPassword } from '../api';

export interface EncVaultModalProps {
  mode: 'init' | 'unlock';
  token: string;
  /** Called with the verified/created password once the server accepts it. */
  onUnlock: (password: string) => void;
  /** User backed out — leave encryption off. */
  onCancel: () => void;
}

const WARNINGS = [
  'This password encrypts files in the browser BEFORE they reach the PC.',
  'It can be set only once and can NEVER be changed or recovered.',
  'Only a verifier hash is stored on the server — the password itself never leaves this device in clear text.',
];

export function EncVaultModal({ mode: initialMode, token, onUnlock, onCancel }: EncVaultModalProps): JSX.Element {
  // Starts in the caller's mode; flips init → unlock if the server answers
  // 409 (password already set — e.g. initialised from another device).
  const [mode, setMode] = useState(initialMode);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (mode === 'init' && password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setPending(true);
    try {
      if (mode === 'init') {
        await setEncPassword(token, password);
      } else {
        const { ok } = await verifyEncPassword(token, password);
        if (!ok) throw new Error('Incorrect encryption password');
      }
      onUnlock(password);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Encryption setup failed';
      if (mode === 'init' && msg.includes('already set')) {
        // Raced by another device — the password exists, so verify instead.
        setMode('unlock');
        setConfirm('');
        setError('A password is already set on this account — enter it below.');
      } else {
        setError(msg);
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Encryption password">
      <form className="card auth-card enc-card" onSubmit={submit}>
        <h2>
          {mode === 'init' ? 'Set up encryption' : 'Unlock encryption'}
        </h2>
        <p className="hint">
          {mode === 'init'
            ? 'Choose a one-time password for the files you upload as .hcloud.'
            : 'This account already has an encryption password. Enter it to encrypt uploads on this device.'}
        </p>
        <ul className="warnings">
          {WARNINGS.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
        <label>
          Encryption password
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            minLength={10}
            required
            placeholder="At least 10 characters"
            autoFocus
          />
        </label>
        {mode === 'init' && (
          <label>
            Confirm password
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              minLength={10}
              required
              placeholder="Repeat the password"
            />
          </label>
        )}
        {error && <div className="banner error">{error}</div>}
        <button className="btn primary" type="submit" disabled={pending}>
          {pending ? 'Please wait…' : mode === 'init' ? 'Set password' : 'Unlock'}
        </button>
        <button type="button" className="btn link" onClick={onCancel}>
          Cancel — upload without encryption
        </button>
      </form>
    </div>
  );
}